import type { ReactNode } from 'react';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuthState } from './AuthContext';
import { useDOITimer } from '../hooks/useDOITimer';
import { authAPI } from '@/lib/api/auth';
import logger from '@/lib/utils/logger';
import { DOIContext, type DOIContextValue } from '@/contexts/DOIContext';

interface DOIProviderProps {
  children: ReactNode;
}

const RESEND_COOLDOWN_SECONDS = 60;

export function DOIProvider({ children }: DOIProviderProps): React.JSX.Element {
  const { user, isAuthenticated } = useAuthState();
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const doiConfig = user?.doi_config;
  const isVerified = user?.is_verified ?? true;
  const isDOIRequired = isAuthenticated && user !== null && !isVerified && doiConfig?.required === true;

  const { timeRemaining, isLocked, formattedTime } = useDOITimer({
    registrationTimestamp: doiConfig?.registration_timestamp ?? null,
    delayMinutes: doiConfig?.delay_minutes ?? 0,
    enabled: isDOIRequired,
  });

  const isModalOpen = isDOIRequired && (isLocked || doiConfig.delay_minutes === 0);

  useEffect((): undefined | (() => void) => {
    if (resendCooldown <= 0) return undefined;
    const interval = setInterval(() => {
      setResendCooldown((previous) => (previous <= 1 ? 0 : previous - 1));
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, [resendCooldown]);

  useEffect((): undefined | (() => void) => {
    if (!resendSuccess) return undefined;
    const timeout = setTimeout(() => {
      setResendSuccess(false);
    }, 3000);
    return () => {
      clearTimeout(timeout);
    };
  }, [resendSuccess]);

  const resendEmail = useCallback(async (): Promise<void> => {
    if (resendCooldown > 0 || isResending) return;
    setIsResending(true);
    setResendSuccess(false);
    try {
      await authAPI.resendVerification();
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setResendSuccess(true);
      logger.info('DOI verification email resent successfully');
    } catch (error) {
      logger.error('Failed to resend DOI verification email:', error);
      setResendCooldown(10);
    } finally {
      setIsResending(false);
    }
  }, [resendCooldown, isResending]);

  const value = useMemo<DOIContextValue>(
    () => ({
      isDOIRequired,
      isLocked,
      timeRemaining,
      formattedTime,
      isModalOpen,
      resendEmail,
      resendCooldown,
      isResending,
      resendSuccess,
    }),
    [
      isDOIRequired,
      isLocked,
      timeRemaining,
      formattedTime,
      isModalOpen,
      resendEmail,
      resendCooldown,
      isResending,
      resendSuccess,
    ]
  );

  return <DOIContext.Provider value={value}>{children}</DOIContext.Provider>;
}
